import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import {
  getBalanceSheet,
  getBranches,
  getCostCenters,
  getProjects,
} from "../../../../Api/apiClient";
import { useLang } from "../../../../Hooks";
import { mapOptionName } from "../../../../utils/localizedText";
import {
  getEndOfMonth,
  transformToTreeTimeSeries,
} from "../../balanceSheetUtils";

const buildDefaultFilters = () => {
  const defaultDate = getEndOfMonth(new Date().toISOString().split("T")[0]);
  return {
    date: defaultDate,
    currency: "SAR",
    period_count: 0,
    group_by: "",
    branches: [],
    costCenters: [],
    projects: [],
    contacts: [],
  };
};

const updateTree = (nodes, rowId) =>
  nodes.map((node) => {
    if (node.id === rowId) return { ...node, isOpen: !node.isOpen };
    if (node.children?.length)
      return { ...node, children: updateTree(node.children, rowId) };
    return node;
  });

const flattenTree = (nodes) => {
  const rows = [];
  const walk = (items) => {
    items.forEach((item) => {
      rows.push(item);
      if (item.children?.length) walk(item.children);
    });
  };
  walk(nodes || []);
  return rows;
};

export const useTimeSeriesCommonSizeData = () => {
  const { texts } = useLang();
  const toast = useToast();

  const [filters, setFilters] = useState(buildDefaultFilters);
  const [reportData, setReportData] = useState({ headers: [], treeData: [] });
  const [isLoadingReport, setIsLoadingReport] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);
  const initialFiltersRef = useRef(filters);

  const fetchReportData = useCallback(
    async (nextFilters) => {
      setIsLoadingReport(true);
      const finalFilters = {
        ...nextFilters,
        date: getEndOfMonth(nextFilters.date),
      };
      if (finalFilters.period_count === 0 || !finalFilters.group_by)
        delete finalFilters.group_by;

      try {
        const response = await getBalanceSheet(finalFilters);
        const apiData = response.data?.rows
          ? response.data
          : { columns: [], rows: [] };
        setReportData(transformToTreeTimeSeries(apiData, texts));
      } catch (error) {
        console.error("Failed to fetch time series common size:", error);
        setReportData({ headers: [], treeData: [] });
        toast({
          title: texts.reports?.error || "Error",
          description:
            error.response?.data?.errors?.[0]?.detail ||
            error.message ||
            texts.reports?.couldNotLoadReport ||
            "Could not load report data.",
          status: "error",
        });
      } finally {
        setIsLoadingReport(false);
      }
    },
    [texts, toast],
  );

  useEffect(() => {
    fetchReportData(initialFiltersRef.current);
  }, [fetchReportData]);

  const loadFilterOptions = useCallback(async () => {
    if (isLoadingFilterOptions) return;
    if (branches.length && projects.length && costCenters.length) return;

    setIsLoadingFilterOptions(true);
    try {
      const [branchesRes, projectsRes, costCentersRes] = await Promise.all([
        getBranches(),
        getProjects(),
        getCostCenters(),
      ]);
      setBranches(
        (branchesRes.data?.results || []).map((item) => mapOptionName(item)),
      );
      setProjects(
        (projectsRes.data?.results || []).map((item) => mapOptionName(item)),
      );
      setCostCenters(
        (costCentersRes.data?.results || []).map((item) => mapOptionName(item)),
      );
    } catch (error) {
      console.error("Failed to load filter options:", error);
      toast({
        title: texts.reports?.error || "Error",
        description:
          texts.reports?.couldNotLoadFilters ||
          "Could not load filter options.",
        status: "error",
      });
    } finally {
      setIsLoadingFilterOptions(false);
    }
  }, [
    branches.length,
    costCenters.length,
    isLoadingFilterOptions,
    projects.length,
    texts,
    toast,
  ]);

  const applyFilters = useCallback(
    (nextFilters) => {
      setFilters((prev) => {
        const merged = { ...prev, ...nextFilters };
        fetchReportData(merged);
        return merged;
      });
    },
    [fetchReportData],
  );

  const changeCurrency = useCallback(
    (currency) => {
      setFilters((prev) => {
        const merged = { ...prev, currency };
        fetchReportData(merged);
        return merged;
      });
    },
    [fetchReportData],
  );

  const toggleRow = useCallback((rowId) => {
    setReportData((prev) => ({
      ...prev,
      treeData: updateTree(prev.treeData || [], rowId),
    }));
  }, []);

  const exportToCSV = useCallback(() => {
    if (!reportData.headers?.length || !reportData.treeData?.length) return;
    const rows = flattenTree(reportData.treeData);
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
    csvContent +=
      [
        texts.reports?.account || "ACCOUNT",
        ...reportData.headers.slice(1),
      ].join(",") + "\r\n";
    rows.forEach((row) => {
      const values = [row.label, ...(row.values || [])].map((value) =>
        typeof value === "number"
          ? value.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : `"${value ?? ""}"`,
      );
      csvContent += values.join(",") + "\r\n";
    });
    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = `${texts.reports?.timeSeriesCommonSize || "time-series-common-size"}_${filters.date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filters.date, reportData.headers, reportData.treeData, texts]);

  const exportToPDF = useCallback(() => {
    if (!reportData.headers?.length || !reportData.treeData?.length) return;
    const rows = flattenTree(reportData.treeData);
    const popup = window.open("", "_blank");
    if (!popup) return;
    popup.document.write(
      `<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8"><title>${texts.reports?.timeSeriesCommonSize || "Time Series Common Size"}</title><style>body{font-family:Arial,sans-serif;margin:20px;color:#333}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:right}th{background:#2c5aa0;color:#fff}</style></head><body><h2>${texts.reports?.timeSeriesCommonSize || "Time Series Common Size"}</h2><p>${texts.reports?.reportDate || "Report date"}: ${filters.date}</p><table><thead><tr><th>${texts.reports?.account || "ACCOUNT"}</th>${reportData.headers
        .slice(1)
        .map((header) => `<th>${header}</th>`)
        .join(
          "",
        )}</tr></thead><tbody>${rows.map((row) => `<tr><td>${row.label}</td>${(row.values || []).map((value) => `<td>${value ?? ""}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`,
    );
    popup.document.close();
    popup.onload = () => popup.print();
  }, [filters.date, reportData.headers, reportData.treeData, texts]);

  const exportReport = useCallback(
    (format) => {
      setIsExporting(true);
      try {
        if (format === "PDF") exportToPDF();
        else exportToCSV();
      } finally {
        setIsExporting(false);
      }
    },
    [exportToCSV, exportToPDF],
  );

  return {
    applyFilters,
    branches,
    changeCurrency,
    costCenters,
    exportReport,
    filters,
    isExporting,
    isLoadingFilterOptions,
    isLoadingReport,
    loadFilterOptions,
    projects,
    reportData,
    toggleRow,
  };
};
